/**
 * OfflineBanner — quiet status indicator at the top of the portal.
 * ────────────────────────────────────────────────────────────────
 * Wave 30 · Phase 7b.
 *
 * Slides down a calm muted-gold band when the user goes offline.
 * Copy: "You're offline. Showing your saved experience."
 *
 * Pulls state from OnlineProvider so every page benefits. The band
 * NEVER auto-dismisses while offline — it's a passive truth-tag, not
 * a notification. Reappears the instant connectivity drops again.
 */
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CloudOff } from "lucide-react";
import { useOnlineState } from "./OnlineProvider";

export default function OfflineBanner() {
  const { isOnline } = useOnlineState();

  return (
    <AnimatePresence>
      {!isOnline ? (
        <motion.div
          key="offline-banner"
          initial={{ y: -32, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -32, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 0.61, 0.36, 1] }}
          className="sticky top-0 z-30 w-full"
          data-testid="offline-banner"
          role="status"
          aria-live="polite"
        >
          {/* Wave 30.4 — Refined to feel ambient, not loud. Reduced
              height, lower contrast, tighter mono. Still visible — quieter. */}
          <div className="bg-[hsl(var(--accent-gold)/0.07)] border-b border-[hsl(var(--accent-gold)/0.20)]">
            <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 py-1.5 flex items-center gap-2">
              <CloudOff
                className="size-3 text-[hsl(var(--accent-gold))]/85"
                strokeWidth={1.5}
              />
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))]/85">
                Offline
              </span>
              <span className="font-accent italic text-[11.5px] sm:text-[12px] text-[hsl(var(--foreground-muted))]">
                Showing your saved experience.
              </span>
            </div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
